/**
 * Energy Flow Drawer Component
 * Shows a live heat pump energy balance diagram in a large drawer.
 */

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Space } from "antd";
import { ArrowLeftOutlined, CaretRightOutlined, PauseOutlined, ThunderboltOutlined } from "@ant-design/icons";

const drawerWidth = typeof window !== "undefined" && window.innerWidth < 1200 ? "90vw" : 860;

/**
 * Format a power value in watts into a compact W/kW label.
 * @param {number} powerWatts - Instantaneous thermal or electrical power in watts.
 * @returns {string} Human-readable power label.
 */
const formatPower = (powerWatts) => {
  if (!Number.isFinite(powerWatts) || powerWatts <= 0) {
    return "0 W";
  }

  if (powerWatts >= 1000) {
    const kilowatts = powerWatts / 1000;
    return `${kilowatts >= 10 ? kilowatts.toFixed(1) : kilowatts.toFixed(2)} kW`;
  }

  return `${Math.round(powerWatts)} W`;
};

/**
 * Format a temperature value for compact SVG labels.
 * @param {number} temperature - Temperature in degrees Celsius.
 * @returns {string} Human-readable temperature label.
 */
const formatTemperature = (temperature) => `${Number.isFinite(temperature) ? temperature.toFixed(1) : "0.0"}°C`;

/**
 * Format a contribution percentage for the energy-flow labels.
 * @param {number} value - Partial power contribution in watts.
 * @param {number} total - Total heating output in watts.
 * @returns {string} Human-readable percentage label.
 */
const formatContributionPercentage = (value, total) => {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
    return "";
  }

  return `${Math.round((value / total) * 100)}%`;
};

const metricCardStyle = {
  padding: "14px 16px",
  minHeight: 86,
};

const formulaSymbolStyle = {
  fontSize: 22,
  fontWeight: 400,
  color: "#0f172a",
  lineHeight: 1,
};

const formulaSubscriptStyle = {
  fontSize: 11,
  fontWeight: 500,
  verticalAlign: "sub",
  marginLeft: 1,
};

/**
 * Small metric card used in the top summary row.
 * Each card displays one live value with a colored dot and a short helper label.
 * @param {Object} props - Component props.
 * @param {string} props.accentColor - Color used for the metric indicator.
 * @param {string} props.label - Short metric label.
 * @param {string} props.value - Formatted metric value.
 * @param {string} props.helper - Helper text under the value.
 * @param {boolean} props.showDivider - Whether to draw a left divider between cards.
 * @returns {JSX.Element} Summary metric card.
 */
function MetricCard({ accentColor, label, value, helper, showDivider }) {
  return (
    <div
      style={{
        ...metricCardStyle,
        borderLeft: showDivider ? "1px solid #e2e8f0" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: 999,
            background: accentColor,
          }}
        />
        <span style={{ color: "#526072", fontSize: 12, fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ color: "#102a43", fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
      <div style={{ color: "#6b7c93", fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>{helper}</div>
    </div>
  );
}

/**
 * Mathematical Q symbol with a typographic subscript.
 * Used to keep the equations visually consistent inside the relation section.
 * @param {Object} props - Component props.
 * @param {string} props.subscript - Subscript rendered next to the Q symbol.
 * @returns {JSX.Element} Styled Q with subscript.
 */
function FormulaQ({ subscript }) {
  return (
    <span style={formulaSymbolStyle}>
      Q
      <span style={formulaSubscriptStyle}>{subscript}</span>
    </span>
  );
}

FormulaQ.propTypes = {
  subscript: PropTypes.string.isRequired,
};

/**
 * Fraction renderer for the COP equation.
 * Keeps numerator and denominator stacked with a visible division bar.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.numerator - Content above the fraction bar.
 * @param {React.ReactNode} props.denominator - Content below the fraction bar.
 * @returns {JSX.Element} Fraction layout.
 */
function Fraction({ numerator, denominator }) {
  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "stretch",
        minWidth: 126,
        verticalAlign: "middle",
      }}
    >
      <span style={{ textAlign: "center", paddingBottom: 4 }}>{numerator}</span>
      <span style={{ borderTop: "1px solid #0f172a", width: "100%" }} />
      <span style={{ textAlign: "center", paddingTop: 4 }}>{denominator}</span>
    </span>
  );
}

Fraction.propTypes = {
  numerator: PropTypes.node.isRequired,
  denominator: PropTypes.node.isRequired,
};

MetricCard.propTypes = {
  accentColor: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  helper: PropTypes.string.isRequired,
  showDivider: PropTypes.bool,
};

MetricCard.defaultProps = {
  showDivider: false,
};

/**
 * Large educational drawer that explains how electrical input and environmental heat
 * combine into useful heating output for the heat pump.
 *
 * The drawer intentionally stays UI-only: it visualizes values already calculated by
 * the simulation without changing any core simulation logic.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.visible - Whether the drawer is open.
 * @param {Function} props.onClose - Called when the drawer should close.
 * @param {boolean} props.isRunning - Whether the heat pump is actively circulating heat.
 * @param {number} props.cop - Current coefficient of performance.
 * @param {number} props.outdoorTemperature - Current outdoor temperature in Celsius.
 * @param {number} props.supplyTemperature - Current supply temperature in Celsius.
 * @param {number} props.returnTemperature - Current return temperature in Celsius.
 * @param {number} props.heatingOutputWatts - Useful heating output in watts.
 * @param {number} props.electricalInputWatts - Electrical input in watts.
 * @param {number} props.environmentalHeatWatts - Environmental heat contribution in watts.
 * @returns {JSX.Element} Energy flow explanation drawer.
 */
export function EnergyFlowDrawer({
  visible,
  onClose,
  onToggleSimulation,
  isRunning,
  isSimulationRunning,
  cop,
  outdoorTemperature,
  supplyTemperature,
  returnTemperature,
  heatingOutputWatts,
  electricalInputWatts,
  environmentalHeatWatts,
}) {
  const { t } = useTranslation();

  const [pausedSnapshot, setPausedSnapshot] = React.useState(null);

  React.useEffect(() => {
    const hasLiveEnergyValues = heatingOutputWatts > 0 || electricalInputWatts > 0 || environmentalHeatWatts > 0;

    if (isSimulationRunning && hasLiveEnergyValues) {
      setPausedSnapshot({
        cop,
        outdoorTemperature,
        supplyTemperature,
        returnTemperature,
        heatingOutputWatts,
        electricalInputWatts,
        environmentalHeatWatts,
      });
    }
  }, [
    isSimulationRunning,
    cop,
    outdoorTemperature,
    supplyTemperature,
    returnTemperature,
    heatingOutputWatts,
    electricalInputWatts,
    environmentalHeatWatts,
  ]);

  const shouldUsePausedSnapshot =
    !isSimulationRunning &&
    pausedSnapshot &&
    heatingOutputWatts <= 0 &&
    electricalInputWatts <= 0 &&
    environmentalHeatWatts <= 0;

  const displayedValues = shouldUsePausedSnapshot
    ? pausedSnapshot
    : {
        cop,
        outdoorTemperature,
        supplyTemperature,
        returnTemperature,
        heatingOutputWatts,
        electricalInputWatts,
        environmentalHeatWatts,
      };

  const displayedFlowActive = isRunning || shouldUsePausedSnapshot;

  const supplyLabel = formatTemperature(displayedValues.supplyTemperature);
  const returnLabel = formatTemperature(displayedValues.returnTemperature);
  const outdoorLabel = formatTemperature(displayedValues.outdoorTemperature);
  const electricalContributionLabel = formatContributionPercentage(displayedValues.electricalInputWatts, displayedValues.heatingOutputWatts);
  const environmentalContributionLabel = formatContributionPercentage(displayedValues.environmentalHeatWatts, displayedValues.heatingOutputWatts);

  return (
    <Drawer
      extra={
        <Button
          size="small"
          icon={isSimulationRunning ? <PauseOutlined /> : <CaretRightOutlined />}
          onClick={onToggleSimulation}
        >
          {isSimulationRunning
            ? t("heatPumpSimulation.controls.pauseSimulation")
            : t("heatPumpSimulation.controls.playSimulation")}
        </Button>
      }
      title={
        <Space direction="vertical" size={0}>
          <Space size="small" align="center">
            <ThunderboltOutlined style={{ color: "#fa8c16", fontSize: 18 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: "#0050b3" }}>
              {t("heatPumpSimulation.modals.energyFlow.drawerTitle")}
            </span>
          </Space>
          <span style={{ fontSize: 12, color: "#666", fontWeight: 400 }}>
            {t("heatPumpSimulation.modals.energyFlow.drawerSubtitle")}
          </span>
        </Space>
      }
      placement="right"
      width={drawerWidth}
      mask={true}
      maskClosable={true}
      styles={{
        mask: {
          background: "transparent",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
        },
      }}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
            gap: 0,
            border: "1px solid #dbe4ee",
            borderRadius: 18,
            overflow: "hidden",
            background: "#f8fbff",
          }}
        >
          <MetricCard
            accentColor="#faad14"
            label={t("heatPumpSimulation.modals.energyFlow.cards.electricalInput")}
            value={formatPower(displayedValues.electricalInputWatts)}
            helper={t("heatPumpSimulation.modals.energyFlow.cards.electricalInputHelper")}
            showDivider={true}
          />
          <MetricCard
            accentColor="#1890ff"
            label={t("heatPumpSimulation.modals.energyFlow.cards.environmentalHeat")}
            value={formatPower(displayedValues.environmentalHeatWatts)}
            helper={t("heatPumpSimulation.modals.energyFlow.cards.environmentalHeatHelper")}
            showDivider={true}
          />
          <MetricCard
            accentColor="#d4380d"
            label={t("heatPumpSimulation.modals.energyFlow.cards.heatingOutput")}
            value={formatPower(displayedValues.heatingOutputWatts)}
            helper={t("heatPumpSimulation.modals.energyFlow.cards.heatingOutputHelper")}
          />
          <MetricCard
            accentColor="#64748b"
            label={t("heatPumpSimulation.modals.energyFlow.cards.cop")}
            value={Number.isFinite(displayedValues.cop) ? displayedValues.cop.toFixed(2) : "0.00"}
            helper={t("heatPumpSimulation.modals.energyFlow.cards.copHelper")}
            showDivider={true}
          />
        </div>

        <div
          style={{
            background: "linear-gradient(180deg, #fffdf8 0%, #fbfdff 100%)",
            borderRadius: 22,
            border: "1px solid #e7e0cf",
            padding: 14,
          }}
        >
          <svg viewBox="0 0 820 330" width="100%" role="img" aria-label={t("heatPumpSimulation.modals.energyFlow.diagram.ariaLabel")}>
            {/* SVG definitions for shared gradients and optional arrowhead markers. */}
            <defs>
              <linearGradient id="energyFlowWarm" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
              <linearGradient id="energyFlowCold" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2563eb" />
                <stop offset="100%" stopColor="#60a5fa" />
              </linearGradient>
              <linearGradient id="energyFlowHot" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="100%" stopColor="#e11d48" />
              </linearGradient>
              <radialGradient id="mixNode" cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#fda4af" />
                <stop offset="55%" stopColor="#fb7185" />
                <stop offset="100%" stopColor="#fb923c" />
              </radialGradient>
              <marker id="blueArrow" viewBox="0 0 10 10" refX="6.2" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
              </marker>
              <marker id="orangeArrow" viewBox="0 0 10 10" refX="6.2" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#f59e0b" />
              </marker>
              <marker id="redArrow" viewBox="0 0 10 10" refX="6.2" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#dc2626" />
              </marker>
            </defs>

            {/* Soft background plate for the diagram area inside the drawer. */}
            <rect x="8" y="8" width="804" height="314" rx="24" fill="rgba(255,255,255,0.45)" />

            {/* COP mini-dashboard mirroring the styling of the main external-unit display. */}
            <g transform="translate(15, 105) scale(1.25)">
              <rect x="0" y="0" width="75" height="97" rx="4" fill="#ffffff" stroke="#bdc3c7" strokeWidth="2" />
              <rect x="1" y="1" width="73" height="18" rx="4" fill="#ecf0f1" />
              <rect x="25" y="7" width="6" height="2" rx="1" fill="#27ae60" opacity="0.8" />
              <rect x="34.5" y="7" width="6" height="2" rx="1" fill="#3498db" opacity="0.8" />
              <rect x="44" y="7" width="6" height="2" rx="1" fill="#95a5a6" opacity="0.6" />
              <text x="12" y="32" fill="#7f8c8d" fontSize="8" fontWeight="600">COP</text>
              <text x="37.5" y="47" fill="#27ae60" fontSize="18" fontWeight="700" textAnchor="middle">
                {Number.isFinite(displayedValues.cop) ? displayedValues.cop.toFixed(2) : "0.00"}
              </text>
              <line x1="5" y1="55" x2="70" y2="55" stroke="#bdc3c7" strokeWidth="1" />
              <circle cx="15" cy="70" r="3" fill="#e74c3c" />
              <text x="23" y="73" fill="#2c3e50" fontSize="9" fontWeight="600">{supplyLabel}</text>
              <circle cx="15" cy="82" r="3" fill="#3498db" />
              <text x="23" y="85" fill="#2c3e50" fontSize="9" fontWeight="600">{returnLabel}</text>
            </g>

            {/* Three main energy streams: outdoor heat, electrical input, and delivered heating output. */}
            <g opacity={displayedFlowActive ? 1 : 0.38}>
              {/* Environmental heat flow entering the heat pump from above-left. */}
              <path
                d="M 322 40 L 462 40 L 462 96 L 486 96 L 450 132 L 414 96 L 438 96 L 438 64 L 322 64 Z"
                fill="url(#energyFlowCold)"
                opacity="0.9"
              />
              {/* Electrical input flow entering the heat pump from below-left. */}
              <path
                d="M 322 325 L 462 325 L 462 269 L 486 269 L 450 233 L 414 269 L 438 269 L 438 301 L 322 301 Z"
                fill="url(#energyFlowWarm)"
                opacity="0.9"
              />
              {/* Resulting heating-output flow leaving the merge point toward the building. */}
              <path
                d="M 497 168 L 641 168 L 641 156 L 700 178 L 641 200 L 641 188 L 497 188 Z"
                fill="url(#energyFlowHot)"
                opacity="0.92"
              />
            </g>

            {/* Environmental heat labels anchored to the upper-left input flow. */}
            <g transform="translate(160, 86)">
              <text x="0" y="0" fill="#1d4ed8" fontSize="18" fontWeight="700">
                {t("heatPumpSimulation.modals.energyFlow.diagram.environmentalHeat")}
              </text>
              <text x="0" y="28" fill="#2563eb" fontSize="24" fontWeight="700">
                {formatPower(displayedValues.environmentalHeatWatts)}
                {environmentalContributionLabel ? (
                  <tspan dx="8" fill="#475569" fontSize="14" fontWeight="600">
                    {environmentalContributionLabel}
                  </tspan>
                ) : null}
              </text>
              <text x="0" y="52" fill="#475569" fontSize="14">
                {t("heatPumpSimulation.modals.energyFlow.diagram.outdoorAir", { temperature: outdoorLabel })}
              </text>
            </g>

            {/* Electrical input labels anchored to the lower-left input flow. */}
            <g transform="translate(160, 237)">
              <text x="0" y="0" fill="#d97706" fontSize="18" fontWeight="700">
                {t("heatPumpSimulation.modals.energyFlow.diagram.electricalInput")}
              </text>
              <text x="0" y="28" fill="#d97706" fontSize="24" fontWeight="700">
                {formatPower(displayedValues.electricalInputWatts)}
                {electricalContributionLabel ? (
                  <tspan dx="8" fill="#475569" fontSize="14" fontWeight="600">
                    {electricalContributionLabel}
                  </tspan>
                ) : null}
              </text>
              <text x="0" y="52" fill="#475569" fontSize="14">
                {t("heatPumpSimulation.modals.energyFlow.diagram.compressorDrive")}
              </text>
            </g>

            {/* Central merge node where both incoming energy streams combine. */}
            <g transform="translate(428, 140)">
                {/* core soft glow */}
                <circle
                    cx="23"
                    cy="42"
                    r="32"
                    fill="#34d399"
                    opacity="0.18"
                />

                {/* plus sign (uniform, lighter thickness, properly centered) */}

                {/* vertical bar */}
                <rect
                    x="20"
                    y="22"
                    width="6"
                    height="40"
                    rx="3"
                    fill="#16a34a"
                    opacity="0.95"
                />

                {/* horizontal bar */}
                <rect
                    x="3"
                    y="39"
                    width="40"
                    height="6"
                    rx="3"
                    fill="#16a34a"
                    opacity="0.95"
                />
            </g>

            {/* Building outline and radiator icon representing the heated space. */}
            <g transform="translate(578, 30)">
              <path d="M -40 247 L -40 42 L 108 0 L 198 42 L 198 247" fill="none" stroke="#b08b59" strokeWidth="7" strokeLinejoin="round" />
              <path d="M 112 16 L 188 52" fill="none" stroke="#caa26b" strokeWidth="5" strokeLinecap="round" />
              
              <g transform="translate(45, 188)">
                <rect x="2" y="4" width="72" height="42" rx="7" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.8" />
                <rect x="6" y="8" width="64" height="34" rx="5" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1" />
                <line x1="14" y1="9" x2="14" y2="41" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="26" y1="9" x2="26" y2="41" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="38" y1="9" x2="38" y2="41" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="50" y1="9" x2="50" y2="41" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="62" y1="9" x2="62" y2="41" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="6" y1="25" x2="70" y2="25" stroke="#94a3b8" strokeWidth="1.7" />
                <line x1="18" y1="46" x2="18" y2="54" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="58" y1="46" x2="58" y2="54" stroke="#64748b" strokeWidth="2.2" strokeLinecap="round" />
                <line x1="11" y1="54" x2="25" y2="54" stroke="#475569" strokeWidth="2.6" strokeLinecap="round" />
                <line x1="51" y1="54" x2="65" y2="54" stroke="#475569" strokeWidth="2.6" strokeLinecap="round" />
                <path d="M 24 -4 C 18 4 18 12 24 18" fill="none" stroke="#dc2626" strokeWidth="3.2" strokeLinecap="round" />
                <path d="M 34 -4 C 28 4 28 12 34 18" fill="none" stroke="#dc2626" strokeWidth="3.2" strokeLinecap="round" />
                <path d="M 44 -4 C 38 4 38 12 44 18" fill="none" stroke="#dc2626" strokeWidth="3.2" strokeLinecap="round" />
              </g>
            </g>

            {/* Heating output labels placed next to the outgoing red flow. */}
            <g transform="translate(595, 112)">
              <text x="0" y="0" fill="#dc2626" fontSize="18" fontWeight="700">
                {t("heatPumpSimulation.modals.energyFlow.diagram.heatingOutput")}
              </text>
              <text x="0" y="28" fill="#dc2626" fontSize="24" fontWeight="700">
                {formatPower(displayedValues.heatingOutputWatts)}
              </text>
            </g>
          </svg>
        </div>

        {/* Neutral explanation block with the heat-balance and COP relations. */}
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 18,
            padding: 18,
            background: "#ffffff",
          }}
        >
          <div style={{ color: "#102a43", fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
            {t("heatPumpSimulation.modals.energyFlow.summary.title")}
          </div>
          <div style={{ color: "#526072", lineHeight: 1.65, marginBottom: 16 }}>
            {t("heatPumpSimulation.modals.energyFlow.summary.text")}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 14,
              alignItems: "stretch",
            }}
          >
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 16 }}>
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                {t("heatPumpSimulation.modals.energyFlow.summary.balanceLabel")}
              </div>
              <div style={{ color: "#0f172a", fontSize: 20, fontWeight: 400, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <FormulaQ subscript="heat" />
                <span>=</span>
                <FormulaQ subscript="electrical" />
                <span>+</span>
                <FormulaQ subscript="environment" />
              </div>
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 16 }}>
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                {t("heatPumpSimulation.modals.energyFlow.summary.copLabel")}
              </div>
              <div style={{ color: "#0f172a", fontSize: 20, fontWeight: 400, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 20, fontWeight: 400 }}>COP</span>
                <span>=</span>
                <Fraction numerator={<FormulaQ subscript="heat" />} denominator={<FormulaQ subscript="electrical" />} />
              </div>
            </div>
          </div>
        </div>
      </Space>
    </Drawer>
  );
}

EnergyFlowDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleSimulation: PropTypes.func.isRequired,
  isRunning: PropTypes.bool.isRequired,
  isSimulationRunning: PropTypes.bool.isRequired,
  cop: PropTypes.number.isRequired,
  outdoorTemperature: PropTypes.number.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  returnTemperature: PropTypes.number.isRequired,
  heatingOutputWatts: PropTypes.number.isRequired,
  electricalInputWatts: PropTypes.number.isRequired,
  environmentalHeatWatts: PropTypes.number.isRequired,
};